import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen } from 'lucide-react';
import { useQuickEdit } from '@/hooks/useQuickEdit';
import { useHomepageSectionMutation } from '@/hooks/useHomepageSectionMutation';
import QuickEditInline from '@/components/quickedit/QuickEditInline';

interface GrowingTipsProps {
  section: any;
}

export default function GrowingTips({ section }: GrowingTipsProps) {
  const content = section.content ?? {};
  const tips: any[] = content.tips ?? [];
  const headingId = `tips-heading-${section.id}`;

  const { isActive } = useQuickEdit();
  const mutation = useHomepageSectionMutation(section.id);

  return (
    <section aria-labelledby={headingId} className="bg-[--bg-primary]">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <h2
            id={headingId}
            className="font-display text-3xl font-bold text-sage-900 sm:text-4xl"
          >
            {isActive ? (
              <QuickEditInline
                value={section.title || 'Tips & Guides'}
                onSave={(val) => mutation.mutateAsync({ title: val, content: section.content })}
                label="Tips section title"
                as="span"
                className="font-display text-3xl font-bold text-sage-900 sm:text-4xl"
              />
            ) : (section.title || 'Tips & Guides')}
          </h2>
          {section.subtitle && (
            <p className="mt-2 text-sage-600 max-w-xl mx-auto">
              {isActive ? (
                <QuickEditInline
                  value={section.subtitle}
                  onSave={(val) => mutation.mutateAsync({ subtitle: val, content: section.content })}
                  label="Growing tips subtitle"
                  as="span"
                  className="text-sage-600"
                />
              ) : section.subtitle}
            </p>
          )}
        </div>

        {tips.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {tips.map((tip: any, i: number) => (
              <div key={i} className="rounded-xl border border-sage-200 bg-surface p-6">
                <BookOpen className="h-6 w-6 text-sage-500 mb-3" aria-hidden="true" />
                <h3 className="font-display font-semibold text-sage-900">
                  {isActive ? (
                    <QuickEditInline
                      value={tip.title}
                      onSave={(val) => {
                        const updated = [...tips];
                        updated[i] = { ...updated[i], title: val };
                        return mutation.mutateAsync({ content: { ...section.content, tips: updated } });
                      }}
                      label={`Growing tip ${i + 1} title`}
                      as="span"
                      className="font-display font-semibold text-sage-900"
                    />
                  ) : tip.title}
                </h3>
                <p className="mt-2 text-sm text-sage-600 leading-relaxed">
                  {isActive ? (
                    <QuickEditInline
                      value={tip.body}
                      onSave={(val) => {
                        const updated = [...tips];
                        updated[i] = { ...updated[i], body: val };
                        return mutation.mutateAsync({ content: { ...section.content, tips: updated } });
                      }}
                      label={`Growing tip ${i + 1} body`}
                      as="span"
                      className="text-sm text-sage-600 leading-relaxed"
                      multiline
                    />
                  ) : tip.body}
                </p>
                {tip.link && (
                  <Link
                    to={tip.link}
                    className="mt-3 inline-flex items-center gap-1 text-sm text-sage-700 hover:text-sage-900 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600 rounded"
                  >
                    Read more
                    <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                  </Link>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center text-sage-500">
            <BookOpen className="mx-auto h-10 w-10 mb-3" aria-hidden="true" />
            <p>Growing tips will appear here once configured.</p>
          </div>
        )}
      </div>
    </section>
  );
}
